// "AS OF" — WHEN THE MONEY MOVED, AS THREE CHIPS.
//
// Every entry screen carried a datetime-local box labelled "When did this
// happen?" with two helper lines under it, and it was left blank on almost
// every entry (0 of 85 September trades typed a time). Blank meant "now", and
// nothing on screen said so. Operator, 12–14 Sept 2026: "too much description
// on each screen is killing the view"; the sweep's answer, agreed 14 Sept: a
// chip row — Now · Yesterday · Pick a time — with "now" selected, so the
// empty case is an explicit choice and the picker only appears when asked for.
//
// The screen keeps its own `when` state (a Dubai wall-clock string, '' = now)
// and its own rules (dubaiWallToIso, backdateProblem); this control only
// renders the choice and the one echo line. Dubai time is implied by the app
// and said once, in the hint.
import { useState } from 'react';
import { BOOKS_OPEN_WALL, backdateProblem, dubaiWallNow, dubaiWallToIso, isoToDubaiWall, fmtWeekday, fmtTime } from '../lib/format';

/** The same wall-clock minute, one day earlier — "yesterday" for a desk that
 *  writes down last night's entries the next morning. */
function dubaiWallYesterday(): string {
  const now = dubaiWallToIso(dubaiWallNow());
  return now ? isoToDubaiWall(new Date(Date.parse(now) - 86_400_000).toISOString()) : '';
}

export function AsOfChips({ value, onChange, disabled, label = 'As of', preset, presetLabel = 'From chain' }: {
  /** Dubai wall-clock (datetime-local form) or '' for now. */
  value: string;
  onChange: (wall: string) => void;
  disabled?: boolean;
  label?: string;
  /** A time that ARRIVED with the screen — a Chain row's block time, a
   *  movement the nudge named. It is its own lit chip, so an operator who
   *  came from that row has nothing left to pick (operator, 15 Sept 2026:
   *  "time & date picker should auto fill to that chain time — I don't need
   *  to select"). Dubai wall-clock, same form as `value`. */
  preset?: string;
  presetLabel?: string;
}) {
  const yesterday = dubaiWallYesterday();
  const isNow = value === '';
  const isYesterday = value !== '' && value.slice(0, 10) === yesterday.slice(0, 10) && value === yesterday;
  const isPreset = !!preset && value === preset;
  // "Pick a time" stays open once chosen, even while the box is being edited.
  const [picking, setPicking] = useState(!isNow && !isYesterday && !isPreset);
  const showPicker = picking || (!isNow && !isYesterday && !isPreset);
  const iso = value ? dubaiWallToIso(value) : '';
  const problem = value ? backdateProblem(value) : null;
  // The one chip (styles.css, emphasis rule): the chosen one is lit.
  const chip = (active: boolean) => `chip ${active ? 'chip-on' : ''}`;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-meta text-tg-hint">{label}</span>
        {/* The one hint the date field carries (§6 "The date field"): it is
            optional, and the clock is the desk's. Nothing else explains it. */}
        <span className="text-meta text-tg-hint opacity-70">optional · Dubai time</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 lg:gap-1">
        <button type="button" disabled={disabled} className={chip(isNow)} onClick={() => { setPicking(false); onChange(''); }}>Now</button>
        {preset && (
          <button type="button" disabled={disabled} className={chip(isPreset)} onClick={() => { setPicking(false); onChange(preset); }}>
            {presetLabel} · {(() => { const i = dubaiWallToIso(preset); return i ? `${fmtWeekday(i)} ${fmtTime(i)}` : preset; })()}
          </button>
        )}
        <button type="button" disabled={disabled || !yesterday} className={chip(isYesterday)} onClick={() => { setPicking(false); onChange(yesterday); }}>Yesterday</button>
        <button type="button" disabled={disabled} className={chip(showPicker)} onClick={() => { setPicking(true); if (isNow) onChange(dubaiWallNow()); }}>Pick a time…</button>
        {showPicker && (
          <input
            type="datetime-local"
            className="input py-1 w-auto"
            value={value}
            min={BOOKS_OPEN_WALL}
            max={dubaiWallNow()}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
      {problem && <p className="text-tg-destructive text-meta">{problem}</p>}
      {/* 21 Oct 2026 audit: a lit "From chain · Wed 21 Oct 2026 10:45" chip IS the
          date — the echo under it said it a second time (a third with the hint's
          "Dubai time"; four lines on a phone). The echo stays for Yesterday and a
          picked time, whose chips do not carry the date. */}
      {!problem && iso && !isPreset && <p className="text-meta text-tg-hint">Books it as {fmtWeekday(iso)}, {fmtTime(iso)} Dubai.</p>}
    </div>
  );
}
